import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  DocumentTextIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/shared/ui";

interface ConsultationRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  lawyerId: string;
  lawyerName: string;
  practiceAreas?: string[];
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  description: string;
  practiceArea: string;
}

export const ConsultationRequestModal: React.FC<
  ConsultationRequestModalProps
> = ({ isOpen, onClose, lawyerId, lawyerName, practiceAreas = [] }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    description: "",
    practiceArea: "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (
      !formData.name ||
      !formData.email ||
      !formData.subject ||
      !formData.description
    ) {
      toast.error(
        t(
          "consultationRequest.errors.requiredFields",
          "Please fill all required fields"
        )
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Import dynamically to avoid circular dependencies
      const { createPublicConsultationRequest } = await import("@/shared/db");

      await createPublicConsultationRequest({
        lawyerId,
        requesterName: formData.name,
        requesterEmail: formData.email,
        requesterPhone: formData.phone || null,
        subject: formData.subject,
        description: formData.description,
        practiceArea: formData.practiceArea || null,
      });

      setIsSuccess(true);
      toast.success(
        t("consultationRequest.success", "Request sent successfully!")
      );

      // Track consultation request
      import("@/utils/analytics").then(({ analytics }) => {
        analytics.consultationRequested();
      });

      // Reset form after delay
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          description: "",
          practiceArea: "",
        });
      }, 3000);
    } catch (error: any) {
      console.error("Error submitting consultation request:", error);
      toast.error(
        error.message ||
          t(
            "consultationRequest.errors.generic",
            "Failed to send request. Please try again."
          )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPracticeAreaName = (code: string) => {
    return t(`lawyer.practiceAreas.${code}`, code);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col"
            >
              {/* Header */}
              <div className="bg-linear-to-r from-tiber-600 to-tiber-800 p-5 text-white shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="h-6 w-6" />
                    <h2 className="text-xl font-bold">
                      {t("consultationRequest.title", "Request Consultation")}
                    </h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <p className="text-tiber-100 text-sm mt-1">
                  {t("consultationRequest.subtitle", {
                    name: lawyerName,
                    defaultValue: "Send a consultation request to {{name}}",
                  })}
                </p>
              </div>

              {/* Content */}
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-8 flex flex-col items-center justify-center text-center"
                >
                  <div className="w-16 h-16 bg-atlantis-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircleIcon className="h-10 w-10 text-atlantis-600" />
                  </div>
                  <h3 className="text-xl font-bold text-juridico mb-2">
                    {t("consultationRequest.successTitle", "Request Sent!")}
                  </h3>
                  <p className="text-tiber-700">
                    {t("consultationRequest.successMessage", {
                      name: lawyerName,
                      defaultValue:
                        "{{name}} will review your request and contact you soon.",
                    })}
                  </p>
                </motion.div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="p-5 space-y-4 overflow-y-auto flex-1"
                >
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-tiber-800 mb-1">
                      {t("consultationRequest.form.name", "Your Name")} *
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-titan" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-2 border border-tiber-200 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500"
                        placeholder={t(
                          "consultationRequest.form.namePlaceholder",
                          "John Doe"
                        )}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-tiber-800 mb-1">
                      {t("consultationRequest.form.email", "Email")} *
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-titan" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-2 border border-tiber-200 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500"
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>

                  {/* Phone (optional) */}
                  <div>
                    <label className="block text-sm font-medium text-tiber-800 mb-1">
                      {t("consultationRequest.form.phone", "Phone")} (
                      {t("common.optional", "Optional")})
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-titan" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 border border-tiber-200 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500"
                        placeholder="+1 (809) 555-0123"
                      />
                    </div>
                  </div>

                  {/* Practice Area (if available) */}
                  {practiceAreas.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-tiber-800 mb-1">
                        {t(
                          "consultationRequest.form.practiceArea",
                          "Legal Area"
                        )}
                      </label>
                      <select
                        name="practiceArea"
                        value={formData.practiceArea}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-tiber-200 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500"
                      >
                        <option value="">
                          {t(
                            "consultationRequest.form.selectArea",
                            "Select an area..."
                          )}
                        </option>
                        {practiceAreas.map((area) => (
                          <option key={area} value={area}>
                            {getPracticeAreaName(area)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-tiber-800 mb-1">
                      {t("consultationRequest.form.subject", "Subject")} *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-tiber-200 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500"
                      placeholder={t(
                        "consultationRequest.form.subjectPlaceholder",
                        "Brief description of your legal need"
                      )}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-tiber-800 mb-1">
                      {t("consultationRequest.form.description", "Description")}{" "}
                      *
                    </label>
                    <div className="relative">
                      <DocumentTextIcon className="absolute left-3 top-3 h-5 w-5 text-titan" />
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full pl-10 pr-4 py-2 border border-tiber-200 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500 resize-none"
                        placeholder={t(
                          "consultationRequest.form.descriptionPlaceholder",
                          "Describe your situation and what kind of help you need..."
                        )}
                      />
                    </div>
                  </div>

                  {/* Privacy Notice */}
                  <p className="text-xs text-titan">
                    {t(
                      "consultationRequest.privacy",
                      "Your information will only be shared with this lawyer to respond to your request."
                    )}
                  </p>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t("consultationRequest.sending", "Sending...")
                        : t("consultationRequest.send", "Send Request")}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
